"use strict";
/**
 * Cross-workspace contract for telling the web app *which* sign-in failure happened, so its copy can differ
 * wherever the remedy does.
 *
 * The codes travel by two routes, hence the two objects below. A sign-in blocking function's rejection reaches
 * the browser as `auth/internal-error` whatever the cause, carrying the function's own message inside the error
 * string, so those codes are matched within that message. Provisioning refuses inside a mutation, so its codes
 * arrive as coded GraphQL errors. Either way the web app keys on the code rather than on prose that could be
 * reworded, which is why they live here instead of as string literals on each side.
 *
 * Of everything these distinguish, only a backend that could not answer is worth retrying — whether Identity
 * Platform timed out on the function or the function could not read the account. Showing "please try again"
 * for any of the rest is a loop that cannot succeed.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMAIL_SESSION_CLAIM = exports.ProvisioningErrorCodes = exports.BlockingFunctionRejectionCodes = void 0;
/** Markers the sign-in blocking function embeds in the rejection Identity Platform relays to the browser. */
exports.BlockingFunctionRejectionCodes = {
    /** The identity provider authenticated the user but published no email address under any name we searched. */
    NO_PROVIDER_EMAIL: 'NEOTAX_NO_PROVIDER_EMAIL',
    /**
     * An enterprise provider would have joined an account that already signs in another way. Refused because an
     * OIDC/SAML provider asserts its own email addresses, so accepting the match would let anyone controlling a
     * configured provider claim an existing account. The user can resolve it by signing in as they did originally.
     */
    ENTERPRISE_LINK_REFUSED: 'NEOTAX_ENTERPRISE_LINK_REFUSED',
    /**
     * The account could not be read to vet an enterprise sign-in, so the sign-in was refused rather than let
     * through unvetted. A failed or slow read, not a conflict: a retry normally succeeds.
     */
    ACCOUNT_UNREADABLE: 'NEOTAX_ACCOUNT_UNREADABLE',
    /**
     * The email address is on a domain we refuse accounts for (BLACKLISTED_DOMAINS — e.g. disposable-email
     * providers). Applied at both account creation and sign-in, so a junk-domain account is turned away
     * before it exists and any that predate the list can no longer sign in.
     */
    BLACKLISTED_DOMAIN: 'NEOTAX_BLACKLISTED_DOMAIN',
};
/**
 * Codes on the GraphQL errors provisioning raises when it will not attach a sign-in to an existing account.
 *
 * Neither code's copy may name the provider the organization requires: reaching this point takes only
 * persuading some identity provider to assert an address, so naming it would tell whoever did that what to
 * configure next. The id belongs on the login audit row.
 */
exports.ProvisioningErrorCodes = {
    /** The organization requires a different enterprise provider than the one signing in. */
    SSO_PROVIDER_MISMATCH: 'SSO_PROVIDER_MISMATCH',
    /** The address belongs to an account whose sign-in identity this is not, for any other reason. */
    ACCOUNT_LINKED_TO_ANOTHER_IDENTITY: 'ACCOUNT_LINKED_TO_ANOTHER_IDENTITY',
};
/**
 * Session claim carrying the address for providers Firebase stored without one.
 *
 * A blocking function cannot write `email` onto the Firebase user record — the reserved fields it may
 * set are `emailVerified`, `displayName`, `photoURL`, `customClaims` and session claims — so for a
 * provider that publishes the address under a non-standard name, `user.email` stays empty and this
 * claim is the only place the address appears. Both the web app reading it off the signed-in session
 * and the backend reading it off the verified token depend on the same name, which is why it lives
 * here rather than as a literal in each.
 *
 * A *session* claim rather than a custom one, so the address lives only in this session's tokens
 * instead of being persisted onto the Firebase user.
 */
exports.EMAIL_SESSION_CLAIM = 'neoTaxEmail';
